import React from "react";

/* Themed decorative SVG ornaments that adapt to the template category.
   Fully vector (never breaks), colored by the template accent. */

const Sprig = ({ className, style }) => (
  <svg viewBox="0 0 100 200" className={className} style={style} fill="none" stroke="currentColor" strokeWidth="1.4">
    <path d="M50 195 C50 150 50 90 50 15" strokeOpacity="0.55" />
    {[0, 1, 2, 3, 4, 5].map((i) => {
      const y = 30 + i * 27;
      return (
        <g key={i}>
          <path d={`M50 ${y} q -24 -10 -37 -1 q 15 13 37 1`} fill="currentColor" fillOpacity="0.22" strokeOpacity="0.4" />
          <path d={`M50 ${y + 13} q 24 -10 37 -1 q -15 13 -37 1`} fill="currentColor" fillOpacity="0.22" strokeOpacity="0.4" />
        </g>
      );
    })}
  </svg>
);

const Flower = ({ className, style }) => (
  <svg viewBox="0 0 60 60" className={className} style={style} fill="currentColor">
    {[0, 1, 2, 3, 4, 5].map((i) => (
      <ellipse key={i} cx="30" cy="15" rx="6.5" ry="14" transform={`rotate(${i * 60} 30 30)`} fillOpacity="0.3" />
    ))}
    <circle cx="30" cy="30" r="5" fillOpacity="0.6" />
  </svg>
);

const Crescent = ({ className, style }) => (
  <svg viewBox="0 0 130 60" className={className} style={style} fill="currentColor">
    <path d="M42 8 a22 22 0 1 0 0 44 a16 16 0 1 1 0 -44" fillOpacity="0.45" />
    <path d="M88 14 l3.2 8.4 l8.8 1 l-6.6 6.2 l2 8.6 l-7.4 -4.4 l-7.4 4.4 l2 -8.6 l-6.6 -6.2 l8.8 -1 z" fillOpacity="0.5" />
  </svg>
);

const Star = ({ className, style }) => (
  <svg viewBox="0 0 20 20" className={className} style={style} fill="currentColor">
    <path d="M10 0 L11.6 8.4 L20 10 L11.6 11.6 L10 20 L8.4 11.6 L0 10 L8.4 8.4 Z" fillOpacity="0.7" />
  </svg>
);

const Filigree = ({ className, style }) => (
  <svg viewBox="0 0 120 120" className={className} style={style} fill="none" stroke="currentColor" strokeWidth="1.2">
    <path d="M8 8 C64 8 84 34 84 84 M8 8 C8 64 34 84 84 84" strokeOpacity="0.45" />
    <path d="M8 8 q34 6 34 34 q -6 -28 -34 -34 Z" fill="currentColor" fillOpacity="0.14" stroke="none" />
    <circle cx="86" cy="86" r="3" fill="currentColor" stroke="none" fillOpacity="0.6" />
  </svg>
);

const Bracket = ({ className, style }) => (
  <svg viewBox="0 0 80 80" className={className} style={style} fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M4 34 V4 H34" strokeOpacity="0.6" />
    <path d="M12 46 V12 H46" strokeOpacity="0.28" />
  </svg>
);

const HeartMini = ({ className, style }) => (
  <svg viewBox="0 0 24 24" className={className} style={style} fill="currentColor">
    <path d="M12 21s-7-4.6-9.4-9C1 8.5 2.6 5 6 5c2 0 3.2 1.2 4 2.4C10.8 6.2 12 5 14 5c3.4 0 5 3.5 3.4 7C19 16.4 12 21 12 21z" fillOpacity="0.4" />
  </svg>
);

export default function TemplateDecor({ cat, accent, compact = false }) {
  const corner = compact ? "w-10" : "w-24 sm:w-32";
  const sprig = compact ? "w-12" : "w-24 sm:w-36";
  const wrap = "pointer-events-none absolute inset-0 z-[1] overflow-hidden";
  const c = { color: accent };

  const botanical = ["Floral", "Tanaman & Bunga"].includes(cat);
  const romantic = cat === "Romantis";
  const nature = cat === "Alam";
  const islamic = cat === "Islami";
  const night = cat === "Malam";
  const ornate = ["Elegan", "Klasik"].includes(cat);
  const geo = ["Industri & Kota", "Modern"].includes(cat);

  if (botanical) return (
    <div className={wrap} style={c} data-testid="decor-botanical">
      <Sprig className={`absolute -left-2 -top-3 ${sprig} rotate-[18deg] opacity-70`} />
      <Sprig className={`absolute -bottom-3 -right-2 ${sprig} rotate-[198deg] opacity-70`} />
      <Flower className={`absolute right-4 top-4 ${compact ? "w-6" : "w-12"} opacity-80`} />
      <Flower className={`absolute bottom-5 left-5 ${compact ? "w-5" : "w-9"} opacity-70`} />
    </div>
  );

  if (romantic) return (
    <div className={wrap} style={c} data-testid="decor-romantic">
      <Sprig className={`absolute -left-3 -top-3 ${sprig} rotate-[20deg] opacity-50`} />
      <HeartMini className={`absolute right-5 top-6 ${compact ? "w-5" : "w-9"}`} />
      <HeartMini className={`absolute bottom-8 left-6 ${compact ? "w-4" : "w-7"}`} />
      <Flower className={`absolute -bottom-2 -right-1 ${compact ? "w-8" : "w-16"} opacity-70`} />
    </div>
  );

  if (nature) return (
    <div className={wrap} style={c} data-testid="decor-nature">
      <Sprig className={`absolute -bottom-4 -left-3 ${sprig} rotate-[-15deg] opacity-70`} />
      <Sprig className={`absolute -bottom-4 -right-3 ${sprig} rotate-[15deg] opacity-70`} />
    </div>
  );

  if (islamic) return (
    <div className={wrap} style={c} data-testid="decor-islamic">
      <Crescent className={`absolute left-1/2 top-4 ${compact ? "w-16" : "w-32"} -translate-x-1/2 opacity-80`} />
      <Star className={`absolute left-6 top-16 ${compact ? "w-3" : "w-5"}`} />
      <Star className={`absolute right-6 top-20 ${compact ? "w-2.5" : "w-4"}`} />
      <div className="absolute inset-x-0 bottom-0 h-16 opacity-[0.12]" style={{ backgroundImage: `repeating-linear-gradient(45deg, ${accent} 0 2px, transparent 2px 14px)` }} />
    </div>
  );

  if (night) return (
    <div className={wrap} style={c} data-testid="decor-night">
      <Crescent className={`absolute right-4 top-4 ${compact ? "w-12" : "w-24"} opacity-70`} />
      {[[10, 30], [30, 12], [70, 20], [85, 45], [20, 60], [55, 8], [92, 70], [8, 80]].map(([x, y], i) => (
        <Star key={i} className={compact ? "w-2" : "w-3.5"} style={{ position: "absolute", left: `${x}%`, top: `${y}%` }} />
      ))}
    </div>
  );

  if (geo) return (
    <div className={wrap} style={c} data-testid="decor-geo">
      <Bracket className={`absolute left-3 top-3 ${corner}`} />
      <Bracket className={`absolute right-3 top-3 ${corner} rotate-90`} />
      <Bracket className={`absolute bottom-3 left-3 ${corner} -rotate-90`} />
      <Bracket className={`absolute bottom-3 right-3 ${corner} rotate-180`} />
    </div>
  );

  // ornate + default (Elegan, Klasik, Minimalis, others)
  return (
    <div className={wrap} style={c} data-testid="decor-ornate">
      <Filigree className={`absolute left-2 top-2 ${corner} opacity-80`} />
      <Filigree className={`absolute right-2 top-2 ${corner} -scale-x-100 opacity-80`} />
      <Filigree className={`absolute bottom-2 left-2 ${corner} -scale-y-100 opacity-70`} />
      <Filigree className={`absolute bottom-2 right-2 ${corner} -scale-100 opacity-70`} />
    </div>
  );
}
